import { useState } from "react";
import { Link } from "react-router-dom";
import { sourceShortLabel } from "../features/leads/leadFilterLabels";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { StatusPill } from "../components/ui/StatusPill";
import { formatRelative } from "../lib/format";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { useMembersIndex } from "../features/team/useMembersIndex";
import {
  useDeletedLeadsQuery,
  useRestoreLead,
  type LeadSummary,
} from "../features/leads/api";

export default function TrashPage() {
  const { current } = useWorkspace();
  const slug = current?.slug ?? null;
  const { data, isLoading, isError, error } = useDeletedLeadsQuery(slug);
  const restore = useRestoreLead(slug);
  const { resolveName } = useMembersIndex();
  const [pendingId, setPendingId] = useState<string | null>(null);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Trash</h1>
          <p className="page-subtitle">
            Soft-deleted leads. Open one to see full details — only restore is
            available from the detail page.
          </p>
        </div>
      </header>

      <Card title="Deleted leads">
        {isLoading ? (
          <div className="skeleton-rows">Loading…</div>
        ) : isError ? (
          <div className="form-error">{(error as Error).message}</div>
        ) : (data?.leads ?? []).length === 0 ? (
          <p className="muted">No leads in the trash.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Phone / Email</th>
                  <th>Assigned to</th>
                  <th>Deleted</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data?.leads ?? []).map((lead: LeadSummary) => (
                  <tr key={lead.id}>
                    <td>
                      <Link
                        to={`/leads/${lead.id}?deleted=1`}
                        className="row-link"
                      >
                        {lead.name ?? "(no name)"}
                      </Link>
                      {(lead.company || lead.rawFields?.subject) ? <div className="row-sub">{(lead.company || lead?.rawFields?.subject) as any}</div> : null}
                    </td>
                    <td><StatusPill status={lead.status} /></td>
                    <td><span className="badge">{sourceShortLabel(lead.source)}</span></td>
                    <td>
                      <div>{lead.phoneE164 ?? "—"}</div>
                      {lead.email ? <div className="row-sub">{lead.email}</div> : null}
                    </td>
                    <td>
                      {lead.assignedTo ? (
                        resolveName(lead.assignedTo)
                      ) : (
                        <span className="muted">Unassigned</span>
                      )}
                    </td>
                    <td title={lead.deletedAt ?? ""}>
                      {lead.deletedAt ? formatRelative(lead.deletedAt) : "—"}
                    </td>
                    <td>
                      <Button
                        type="button"
                        size="sm"
                        disabled={restore.isPending && pendingId === lead.id}
                        onClick={() => {
                          setPendingId(lead.id);
                          restore.mutate(lead.id, {
                            onSettled: () => setPendingId(null),
                          });
                        }}
                      >
                        {restore.isPending && pendingId === lead.id
                          ? "Restoring…"
                          : "Restore"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {restore.isError ? (
          <div className="form-error">{(restore.error as Error).message}</div>
        ) : null}
      </Card>
    </div>
  );
}
